import { Nullable } from "./nullable";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { PhoneNumberUtil } from "google-libphonenumber";

export type CustomerType = "individual" | "business" | "association" | "public";

export type CustomerIdentityFields = {
  firstName?: string;
  lastName?: string;
  businessName?: string;
};

type CustomerIdentitySource = {
  type?: CustomerType | string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
};

function hasNonEmptyTrimmedText(value: string | null | undefined): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

export function hasValidCustomerIdentity(
  customer: CustomerIdentitySource | null | undefined
): boolean {
  if (!customer) {
    return false;
  }

  switch (customer.type) {
    case "business":
    case "association":
    case "public": {
      return hasNonEmptyTrimmedText(customer.businessName);
    }
    default: {
      return (
        hasNonEmptyTrimmedText(customer.firstName) &&
        hasNonEmptyTrimmedText(customer.lastName)
      );
    }
  }
}

export function getCustomerIdentityFields(
  customer: CustomerIdentitySource
): CustomerIdentityFields {
  switch (customer.type) {
    case "business":
    case "association":
    case "public": {
      return {
        businessName: customer.businessName || undefined
      };
    }
    default: {
      return {
        firstName: customer.firstName || undefined,
        lastName: customer.lastName || undefined
      };
    }
  }
}

export function getCustomerTypes(): Array<{
  display: string;
  value: CustomerType;
}> {
  return [
    {
      value: "individual",
      display: sntTranslate(marker("Privato"))
    },
    {
      value: "business",
      display: sntTranslate(marker("Azienda"))
    },
    {
      value: "association",
      display: sntTranslate(marker("Associazione"))
    },
    {
      value: "public",
      display: sntTranslate(marker("Pubblico"))
    }
  ];
}

export function getPrefixNumber(): Array<{
  display: string;
  icon: string;
  value: string;
}> {
  return PhoneNumberUtil.getInstance()
    .getSupportedRegions()
    .map((r) => {
      return {
        value: r.toLowerCase(),
        icon: "mr-1 fi fi-" + r.toLowerCase(),
        display:
          "+" +
          PhoneNumberUtil.getInstance().getCountryCodeForRegion(r).toString()
      };
    });
}

export function getCustomerNominative(
  customer: Nullable<any>,
  inverted: boolean = false,
  firstNameInitial: boolean = false
): string {
  if (!customer) {
    return "";
  }

  switch (customer.type) {
    case "business": {
      return customer.businessName || "";
    }
    case "association": {
      return customer.businessName || "";
    }
    case "public": {
      return customer.businessName || "";
    }
    case "individual": {
      return inverted
        ? `${customer.lastName || ""} ${getFirstName(
            customer,
            firstNameInitial
          )}`.trim()
        : `${getFirstName(customer, firstNameInitial)} ${
            customer.lastName || ""
          }`.trim();
    }
    default: {
      const privateName = inverted
        ? `${customer.lastName || ""} ${getFirstName(
            customer,
            firstNameInitial
          )}`.trim()
        : `${getFirstName(customer, firstNameInitial)} ${
            customer.lastName || ""
          }`.trim();

      return `${customer.businessName || ""} ${privateName}`.trim();
    }
  }
}

function getFirstName(customer: any, firstNameInitial: boolean): string {
  const firstName = customer.firstName || "";
  return firstNameInitial ? `${firstName[0] || ""}.` : firstName;
}

export function customerIcon(customer: { type: string }): string {
  if (!customer) {
    return "fa-light fa-question-circle";
  }

  switch (customer.type) {
    case "business": {
      return "fa-light fa-building";
    }
    case "individual": {
      return "fa-light fa-user";
    }
    case "association": {
      return "fa-light fa-people-line";
    }
    case "public": {
      return "fa-light fa-building-columns";
    }
    default: {
      return "fa-light fa-question-circle";
    }
  }
}
